/**
 * InviteRedeemPage.tsx — ɳSentry invite link redemption page
 *
 * Purpose:    Validates invite tokens from ?token= query param, displays
 *             team info, and confirms the invite. Handles expired/invalid tokens.
 * Inputs:     token from URL search params
 * Outputs:    Invite details UI with accept/decline actions
 * Constraints: Token validation is server-side; client just renders the response
 */
'use client'

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'

// UI State machine for /invite/[code]:
// loading      → verifying code
// valid        → code is good, show account creation form
// invalid      → code not found or expired
// used         → code already redeemed
// ratelimited  → too many redemption attempts
// offline      → no network connection
// submitted    → account created successfully

type State = 'loading' | 'valid' | 'invalid' | 'used' | 'ratelimited' | 'offline' | 'submitted'

interface Props {
  code: string
}

export function InviteRedeemPage({ code }: Props) {
  const [state, setState] = useState<State>('loading')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const verifyCode = useCallback(async () => {
    if (!navigator.onLine) {
      setState('offline')
      return
    }

    try {
      const res = await fetch(`/api/invite/verify?code=${encodeURIComponent(code)}`)
      if (res.status === 429) {
        setState('ratelimited')
        return
      }
      if (res.status === 410) {
        setState('used')
        return
      }
      if (!res.ok) {
        setState('invalid')
        return
      }
      setState('valid')
    } catch {
      if (!navigator.onLine) {
        setState('offline')
      } else {
        setState('invalid')
      }
    }
  }, [code])

  useEffect(() => {
    verifyCode()
  }, [verifyCode])

  async function handleCreateAccount(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      const res = await fetch('/api/invite/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, name, email, password }),
      })
      if (res.ok) {
        setState('submitted')
      } else if (res.status === 410) {
        setState('used')
      } else {
        setState('invalid')
      }
    } catch {
      setState('offline')
    } finally {
      setSubmitting(false)
    }
  }

  // Loading
  if (state === 'loading') {
    return (
      <div className="flex flex-col items-center gap-4 py-12 text-center">
        <svg className="h-8 w-8 animate-spin text-indigo-700 dark:text-indigo-400" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <p className="text-brand-muted">Verifying your invite code...</p>
      </div>
    )
  }

  // Offline
  if (state === 'offline') {
    return (
      <div className="rounded-xl border border-brand-subtle bg-brand-surface/50 p-8 text-center">
        <h3 className="mb-2 text-lg font-bold text-brand-ink">No internet connection</h3>
        <p className="mb-4 text-sm text-brand-muted">
          Invite code verification requires a connection. Please check your network and try again.
        </p>
        <button
          onClick={() => { setState('loading'); verifyCode() }}
          className="rounded-md border border-brand-card px-4 py-2 text-sm text-brand-secondary hover:bg-brand-surface-2 hover:text-brand-ink transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  // Invalid / expired
  if (state === 'invalid') {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/20 p-8 text-center">
        <h3 className="mb-2 text-lg font-bold text-red-700 dark:text-red-300">This invite has expired</h3>
        <p className="mb-6 text-sm text-brand-muted">
          This invite code is invalid or has expired. Ask the person who invited you for a new one, or join the general waitlist.
        </p>
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Link
            to="/invite"
            className="rounded-md bg-brand-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-hover transition-colors"
          >
            Request a new invite
          </Link>
          <Link
            to="/invite"
            className="text-sm text-brand-muted hover:text-brand-ink transition-colors"
          >
            Join waitlist instead
          </Link>
        </div>
      </div>
    )
  }

  // Already used — show login prompt
  if (state === 'used') {
    return (
      <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 p-8 text-center">
        <h3 className="mb-2 text-lg font-bold text-amber-700 dark:text-amber-300">Invite already used</h3>
        <p className="mb-6 text-sm text-brand-muted">
          This invite code has already been redeemed. If you already created an account, sign in to your ɳFamily instance. If someone else used your code, contact the family admin.
        </p>
        <p className="text-xs text-brand-muted">
          Need a new code?{' '}
          <Link to="/invite" className="text-indigo-700 dark:text-indigo-400 hover:text-indigo-700 dark:text-indigo-300">
            Join the waitlist
          </Link>
        </p>
      </div>
    )
  }

  // Rate limited
  if (state === 'ratelimited') {
    return (
      <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 p-8 text-center">
        <h3 className="mb-2 text-lg font-bold text-amber-700 dark:text-amber-300">Too many attempts</h3>
        <p className="text-sm text-brand-muted">
          Too many redemption attempts for this code. Please wait 24 hours before trying again, or contact{' '}
          <a href="mailto:info@nself.org" className="text-indigo-700 dark:text-indigo-400 hover:text-indigo-700 dark:text-indigo-300">
            info@nself.org
          </a>
          .
        </p>
      </div>
    )
  }

  // Submitted successfully
  if (state === 'submitted') {
    return (
      <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/20 p-8 text-center">
        <div className="mb-4 flex justify-center">
          <svg className="h-12 w-12 text-emerald-700 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
        </div>
        <h3 className="mb-2 text-xl font-bold text-brand-ink">Account created</h3>
        <p className="text-brand-muted">
          Welcome to ɳFamily, {name || 'there'}. Check your inbox for setup instructions from the family admin.
        </p>
      </div>
    )
  }

  // Valid code — show account creation form
  return (
    <div>
      <div className="mb-6 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-950/20 p-4 text-sm text-emerald-700 dark:text-emerald-300">
        Invite code verified. Create your account below to join.
      </div>
      <form onSubmit={handleCreateAccount} className="space-y-4">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-brand-secondary">
            Your name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Jane Smith"
            className="w-full rounded-md border border-brand-card bg-brand-surface px-4 py-2.5 text-brand-ink placeholder-gray-600 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-brand-primary"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-brand-secondary">
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
            className="w-full rounded-md border border-brand-card bg-brand-surface px-4 py-2.5 text-brand-ink placeholder-gray-600 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-brand-primary"
          />
        </div>
        <div>
          <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-brand-secondary">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            placeholder="8+ characters"
            className="w-full rounded-md border border-brand-card bg-brand-surface px-4 py-2.5 text-brand-ink placeholder-gray-600 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-brand-primary"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-brand-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-hover transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Creating account...' : 'Create account'}
        </button>
      </form>
    </div>
  )
}
